import {LINKER_PAYLOAD_LEAVING_GROUP_MALFORMED_ERROR} from 'molbio/adcs/linker-payload-connection/AdcLinkerPayloadLeavingGroupConstants';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

/**
 * Chemistry Service caps each bond a leaving group breaks with an H, so a lone H leaving group comes back as H2.
 * Chemists expect to see it as the H that leaves.
 */
const LONE_HYDROGEN_SMILES = '[H][H]';
const LONE_HYDROGEN_DISPLAY = 'H';

/**
 * Display label for a leaving group by its number, e.g. "LG1", matching complex polymer attachments, followed by
 * its SMILES when given, e.g. "LG2 (ON1C(=O)CCC1=O)", or "LG3 (H)" for a lone H. The label without SMILES is the
 * text stored in a Linker-payload leaving group cell.
 */
export function formatLeavingGroup(leavingGroup: number, smiles: string | null = null): string {
  const label = `LG${leavingGroup}`;
  if (!smiles) {
    return label;
  }
  return `${label} (${smiles === LONE_HYDROGEN_SMILES ? LONE_HYDROGEN_DISPLAY : smiles})`;
}

const LEAVING_GROUP_PATTERN = /^LG([1-9]\d*)$/;

/**
 * A parsed Linker-payload leaving group cell: its leaving group's number, null for an empty cell, or an error
 * for text that is not a leaving group label.
 */
export type ParsedLeavingGroup = {leavingGroup: number | null} | {error: string};

const MALFORMED: ParsedLeavingGroup = {error: LINKER_PAYLOAD_LEAVING_GROUP_MALFORMED_ERROR};

/** Parses the text stored in a Linker-payload leaving group cell, which may be hand-typed or pasted. */
export function parseLeavingGroup(text: string): ParsedLeavingGroup {
  const trimmed = text.trim();
  if (trimmed === '') {
    return {leavingGroup: null};
  }
  const match = LEAVING_GROUP_PATTERN.exec(trimmed);
  const leavingGroup = match != null ? Number(match[1]) : null;
  // Numbers too large to represent exactly would format back to a different label.
  return leavingGroup != null && Number.isSafeInteger(leavingGroup) ? {leavingGroup} : MALFORMED;
}

/** Parses a Linker-payload leaving group cell's typed value; see {@link parseLeavingGroup}. */
export function parseLeavingGroupTypedValue(typedValue: TypedValue | null | undefined): ParsedLeavingGroup {
  if (typedValue == null) {
    return {leavingGroup: null};
  }
  return typedValue.type === BaseTypedValueType.STRING ? parseLeavingGroup(typedValue.rawValue) : MALFORMED;
}
